import { useCallback, useEffect, useRef } from 'react'

import Api from '../api'

interface UseSessionMonitorOptions {
  accessToken?: string
  isAuthenticated: boolean
  checkIntervalMs?: number
  enabled?: boolean
}

/**
 * Hook to monitor server-side session validity by periodically checking with the server.
 * This proactively logs out users when their server session expires (typically after 5 minutes).
 */
const useSessionMonitor = ({
  accessToken,
  isAuthenticated,
  checkIntervalMs = 60000, // Check every 60 seconds
  enabled = true,
}: UseSessionMonitorOptions) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  const isCheckingRef = useRef(false)
  const lastCheckTimeRef = useRef<number>(0)

  const checkSession = useCallback(async () => {
    if (!accessToken || !isAuthenticated || !enabled || isCheckingRef.current) {
      return
    }

    const now = Date.now()
    // Prevent too frequent checks (minimum 30 seconds between checks)
    if (now - lastCheckTimeRef.current < 30000) {
      return
    }

    isCheckingRef.current = true
    lastCheckTimeRef.current = now

    try {
      await Api.get('/api/users/me')
    } catch {
      // Don't logout on network errors, only on 401s (handled by the API utility)
    } finally {
      isCheckingRef.current = false
    }
  }, [accessToken, isAuthenticated, enabled])

  const startMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    if (!isAuthenticated || !enabled) {
      return
    }

    // Initial check after a short delay
    setTimeout(checkSession, 5000)

    // Set up periodic checks
    intervalRef.current = setInterval(checkSession, checkIntervalMs)
  }, [checkSession, checkIntervalMs, isAuthenticated, enabled])

  const stopMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = undefined
    }
    isCheckingRef.current = false
  }, [])

  useEffect(() => {
    if (isAuthenticated && enabled) {
      startMonitoring()
    } else {
      stopMonitoring()
    }

    return stopMonitoring
  }, [isAuthenticated, enabled, startMonitoring, stopMonitoring])

  // Cleanup on unmount
  useEffect(() => stopMonitoring, [stopMonitoring])

  return {
    checkSession,
    startMonitoring,
    stopMonitoring,
  }
}

export default useSessionMonitor
